import {
  buildCookieConsent,
  clearCookieConsent,
  COOKIE_CATEGORIES,
  readCookieConsent,
  writeCookieConsent,
  type CookieConsentPreferences,
} from '~/utils/cookieConsent'

export function useCookieConsent() {
  const shopSlug = useShopSlug()
  const consent = useState<CookieConsentPreferences | null>('cookie-consent', () => null)
  const hydrated = useState('cookie-consent-hydrated', () => false)
  const bannerOpen = useState('cookie-consent-banner-open', () => false)
  const preferencesOpen = useState('cookie-consent-preferences-open', () => false)

  const analytics = computed(() => consent.value?.analytics ?? false)
  const marketing = computed(() => consent.value?.marketing ?? false)
  const hasDecided = computed(() => consent.value !== null)

  function hydrate() {
    if (!import.meta.client) return
    consent.value = readCookieConsent(shopSlug.value)
    hydrated.value = true
    bannerOpen.value = !consent.value
    preferencesOpen.value = false
  }

  function persist(preferences: CookieConsentPreferences) {
    writeCookieConsent(preferences, shopSlug.value)
    consent.value = preferences
    bannerOpen.value = false
    preferencesOpen.value = false
  }

  function acceptAll() {
    persist(buildCookieConsent(true, true))
  }

  function rejectOptional() {
    persist(buildCookieConsent(false, false))
  }

  function savePreferences(analyticsEnabled: boolean, marketingEnabled: boolean) {
    persist(buildCookieConsent(analyticsEnabled, marketingEnabled))
  }

  function openPreferences() {
    if (!import.meta.client) return
    hydrate()
    bannerOpen.value = true
    preferencesOpen.value = true
  }

  function closeBanner() {
    if (!hasDecided.value) return
    bannerOpen.value = false
    preferencesOpen.value = false
  }

  function resetConsent() {
    clearCookieConsent(shopSlug.value)
    consent.value = null
    bannerOpen.value = true
    preferencesOpen.value = false
  }

  watch(shopSlug, () => {
    hydrated.value = false
    hydrate()
  })

  return {
    consent,
    hydrated,
    bannerOpen,
    preferencesOpen,
    analytics,
    marketing,
    hasDecided,
    categories: COOKIE_CATEGORIES,
    hydrate,
    acceptAll,
    rejectOptional,
    savePreferences,
    openPreferences,
    closeBanner,
    resetConsent,
  }
}
